import { useEffect, useRef, useState } from "react";

type UseElapsedTimeOptions = {
  /** Run the timer while true; resets to 0 and stops ticking when false. */
  active: boolean;
  /** Epoch ms to measure from. Defaults to when the timer became active. */
  startAt?: number;
  /** How often to re-render with a fresh elapsed value. */
  intervalMs?: number;
};

/**
 * Milliseconds elapsed since `startAt` (defaulting to when the timer became
 * active), re-rendering every `intervalMs` while `active`. Returns 0 while
 * inactive, so callers gate it on a pending/loading flag and read one
 * monotonic value to drive time-based UI — staged copy, "taking longer"
 * fallbacks, soft timeouts.
 */
export function useElapsedTime({
  active,
  startAt,
  intervalMs = 1000,
}: UseElapsedTimeOptions): number {
  const [elapsedMs, setElapsedMs] = useState(0);

  // Hold startAt in a ref so a value that lands mid-run (e.g. the server's
  // creation_started_at arriving a few renders after polling begins) doesn't
  // tear down and restart the interval — which would flash elapsed back to 0.
  // The start instant is captured once per activation; only `active` and
  // `intervalMs` restart the timer.
  const startAtRef = useRef(startAt);
  useEffect(() => {
    startAtRef.current = startAt;
  }, [startAt]);

  useEffect(() => {
    if (!active) {
      setElapsedMs(0);
      return;
    }
    const start = startAtRef.current ?? Date.now();
    const tick = () => setElapsedMs(Date.now() - start);
    tick();
    const id = setInterval(tick, intervalMs);
    return () => clearInterval(id);
  }, [active, intervalMs]);

  return elapsedMs;
}
